import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import apiService from '../services/api';
import ChunkCard from '../components/ChunkCard';
import Toast from '../components/Toast';
import { useSession } from '../contexts/SessionContext';
import { useOffline } from '../contexts/OfflineContext';
import { useGuidedTour } from '../contexts/GuidedTourContext';
import { Chunk } from '../types';
import './SearchChunksPage.css';
import '../components/ActionButtons.css';

// Special marker to indicate user is viewing "Latest" chunks
const LATEST_VIEW_MARKER = '__LATEST__';

const SearchChunksPage = () => {
  const {
    markChunkSelected,
    unmarkChunkSelectedConditionally,
    markChunkDiscarded,
    currentSession,
    currentSessionId,
    userId,
    updateSession,
    registerSearchRefreshCallback,
    unregisterSearchRefreshCallback
  } = useSession();
  const { isActive: isTourActive, getCurrentStep } = useGuidedTour();
  const { isOnline } = useOffline();

  const [query, setQuery] = useState(currentSession?.lastSearchQuery ?? '');
  const [source, setSource] = useState(currentSession?.lastSearchFilters?.source ?? '');
  const [includeDone, setIncludeDone] = useState(currentSession?.lastSearchFilters?.includeDone ?? false);
  const [includeDiscarded, setIncludeDiscarded] = useState(currentSession?.lastSearchFilters?.includeDiscarded ?? false);
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingDone, setIsMarkingDone] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(currentSessionId);
  const [lastUserId, setLastUserId] = useState(userId);
  const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({});
  const [hasLoadedInitialSearch, setHasLoadedInitialSearch] = useState(false);
  const [userSources, setUserSources] = useState<string[]>(['diary']);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Demo chunk for guided tour
  const demoChunk = useMemo<Chunk>(() => ({
    id: 'demo-chunk-tour',
    score: 0.95,
    metadata: {
      text: 'This is a demo chunk to show you how the search results look! In a real search, you would see relevant content from your uploaded entries. You can use the action buttons below or keyboard shortcuts: → to select, ← to discard, D to delete, or M to mark as done.',
      source: 'tutorial',
      done: false,
      timestamp: new Date().toISOString(),
    },
  }), []);

  // Load user sources on mount
  useEffect(() => {
    const loadSources = async () => {
      try {
        const response = await apiService.getUserSources(userId);
        setUserSources(response.data.sources);
      } catch (error) {
        console.error('Error loading sources:', error);
        // Keep default sources if loading fails
      } finally {
        setSourcesLoading(false);
      }
    };
    loadSources();
  }, [userId]);

  // Helper function to execute search based on session's lastSearchQuery
  const executeSearchFromSession = useCallback(async (session: typeof currentSession) => {
    // Skip if no search query is set (null/undefined); empty string is technically a valid query but unlikely
    if (!session?.lastSearchQuery) return;

    // Check if user was viewing "Latest" chunks
    if (session.lastSearchQuery === LATEST_VIEW_MARKER) {
      const response = await apiService.getLatestChunks(150, userId);
      setChunks(response.data.matches);
      // Restore the saved index, ensuring it's within bounds
      const savedIndex = session.lastSearchIndex ?? 0;
      const validIndex = Math.min(savedIndex, Math.max(0, response.data.matches.length - 1));
      setCurrentIndex(validIndex >= 0 ? validIndex : 0);
    } else {
      // Regular search query
      const response = await apiService.queryChunks(
        session.lastSearchQuery,
        {
          sourceFilter: session.lastSearchFilters?.source || null,
          includeDone: session.lastSearchFilters?.includeDone || false,
          includeDiscarded: session.lastSearchFilters?.includeDiscarded || false,
        },
        currentSessionId,
        userId
      );

      setChunks(response.data.matches);
      // Restore the saved index, ensuring it's within bounds
      const savedIndex = session.lastSearchIndex ?? 0;
      const validIndex = Math.min(savedIndex, Math.max(0, response.data.matches.length - 1));
      setCurrentIndex(validIndex >= 0 ? validIndex : 0);
    }
  }, [currentSessionId, userId]);

  // Load demo chunk when tour is active and on search page
  useEffect(() => {
    if (isTourActive) {
      const currentStep = getCurrentStep();
      // Check if we're on a search page step that should show chunks
      if (currentStep?.page === '/search' &&
          (currentStep?.id === 'chunk-cards' ||
           currentStep?.id === 'discard-button' ||
           currentStep?.id === 'select-button' ||
           currentStep?.id === 'delete-button' ||
           currentStep?.id === 'mark-done-button')) {
        setChunks([demoChunk]);
        setCurrentIndex(0);
        setHasSearched(true);
      }
    }
  }, [isTourActive, getCurrentStep, demoChunk]);

  // Auto-search on component mount if there's a lastSearchQuery
  useEffect(() => {
    const performInitialAutoSearch = async () => {
      // Skip auto-search if tour is active
      if (isTourActive) return;

      // Only run once when currentSession is available and we haven't loaded yet
      if (!currentSession?.lastSearchQuery || hasLoadedInitialSearch) return;

      setHasLoadedInitialSearch(true);
      setLoading(true);
      setHasSearched(true);

      try {
        await executeSearchFromSession(currentSession);
      } catch (error) {
        console.error('Initial auto-search error:', error);
      } finally {
        setLoading(false);
      }
    };

    performInitialAutoSearch();
  // Run when currentSession becomes available
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, currentSessionId, userId, isTourActive]);

  // Register callback for auto-refresh when switching sessions
  useEffect(() => {
    const refreshSearch = async () => {
      if (!currentSession?.lastSearchQuery) return;

      setLoading(true);
      setHasSearched(true);

      try {
        await executeSearchFromSession(currentSession);
      } catch (error) {
        console.error('Auto-refresh search error:', error);
      } finally {
        setLoading(false);
      }
    };

    registerSearchRefreshCallback(refreshSearch);

    return () => {
      unregisterSearchRefreshCallback();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, currentSessionId, userId]);

  // Restore search filters when session changes OR when userId changes
  useEffect(() => {
    // Only clear chunks if the session ID or user ID actually changed
    const sessionChanged = currentSessionId !== lastSessionId;
    const userChanged = userId !== lastUserId;

    if (sessionChanged || userChanged) {
      setChunks([]);
      setCurrentIndex(0);
      setHasSearched(false);
      setHasLoadedInitialSearch(false); // Reset flag so auto-search can run again
      setLastSessionId(currentSessionId);
      setLastUserId(userId);
    }

    if (!currentSession) return;

    // Ensure the current session belongs to the current user to prevent cross-contamination
    // when switching users (the session data might be stale during the transition)
    if (currentSession.user_id !== userId) return;

    const lastSearchQuery = currentSession.lastSearchQuery || '';
    const lastSearchFilters = currentSession.lastSearchFilters || {
      source: '',
      includeDone: false,
      includeDiscarded: false,
    };

    // Restore search filters
    // Don't show the LATEST_VIEW_MARKER in the search input
    const displayQuery = lastSearchQuery === LATEST_VIEW_MARKER ? '' : lastSearchQuery;
    setQuery(displayQuery);
    setSource(lastSearchFilters.source);
    setIncludeDone(lastSearchFilters.includeDone);
    setIncludeDiscarded(lastSearchFilters.includeDiscarded);
  // Remove chunks.length from dependencies to prevent clearing after search
  // Add userId to dependencies to ensure search state updates when user changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, currentSession, userId]);

  // Persist currentIndex to session when it changes (for search results)
  useEffect(() => {
    // Only persist if we have chunks and a valid session
    if (chunks.length === 0 || !currentSessionId || !hasSearched) return;

    // Skip persisting during tour
    if (isTourActive) return;

    updateSession(currentSessionId, {
      lastSearchIndex: currentIndex
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);


  const handleSearch = async () => {
    // Don't allow search during tour
    if (isTourActive) return;

    if (!query.trim()) {
      return;
    }

    // Blur the search input to close the keyboard on mobile
    searchInputRef.current?.blur();

    setLoading(true);
    setHasSearched(true); // Mark that a search has been performed

    try {
      const response = await apiService.queryChunks(query, {
        sourceFilter: source || null,
        includeDone,
        includeDiscarded,
      }, currentSessionId, userId);

      // The backend handles session-based filtering
      const matchesData = response.data.matches || [];
      setChunks(matchesData);
      setCurrentIndex(0);
      setScrollPositions({}); // Clear scroll positions when new search is performed

      // Update session with search query and filters so they persist
      updateSession(currentSessionId, {
        lastSearchQuery: query,
        lastSearchIndex: 0,
        lastSearchFilters: {
          source,
          includeDone,
          includeDiscarded,
        }
      });
    } catch (error: unknown) {
      console.error('Search error:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorDetail = err.response?.data?.detail || err.message || 'An error occurred during search';
      setToast({ message: `Search error: ${errorDetail}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGetLatest = async () => {
    // Don't allow getting latest during tour
    if (isTourActive) return;

    // Blur the search input to close the keyboard on mobile
    searchInputRef.current?.blur();

    setLoading(true);
    setHasSearched(true); // Mark that a search has been performed

    try {
      const response = await apiService.getLatestChunks(150, userId);

      const matchesData = response.data.matches || [];
      setChunks(matchesData);
      setCurrentIndex(0);
      setScrollPositions({}); // Clear scroll positions when new search is performed

      // Save special marker to indicate we're viewing latest chunks
      // This allows the view to be restored when user logs back in
      setQuery('');
      updateSession(currentSessionId, {
        lastSearchQuery: LATEST_VIEW_MARKER,
        lastSearchIndex: 0,
        lastSearchFilters: {
          source: '',
          includeDone: false,
          includeDiscarded: false,
        }
      });

      // Don't show toast for successful latest chunks load
    } catch (error: unknown) {
      console.error('Get latest error:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorDetail = err.response?.data?.detail || err.message || 'An error occurred getting latest chunks';
      setToast({ message: `Error: ${errorDetail}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const currentChunk = chunks[currentIndex];

  const handleSwipeLeft = useCallback(async () => {
    if (!currentChunk) return;

    // Optimistically update UI immediately
    const newChunks = chunks.filter((_, index) => index !== currentIndex);
    setChunks(newChunks);

    // Adjust index if needed
    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Update Pinecone metadata to mark as discarded for this session (in background)
    try {
      await markChunkDiscarded(currentChunk.id);
    } catch (error) {
      console.error('Error marking chunk as discarded:', error);
      // On error, restore the chunk to the list
      setChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    }
  }, [currentChunk, chunks, currentIndex, markChunkDiscarded]);

  const handleSwipeRight = useCallback(async () => {
    if (!currentChunk) return;

    // Optimistically update UI immediately
    const newChunks = chunks.filter((_, index) => index !== currentIndex);
    setChunks(newChunks);

    // Adjust index if needed
    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Update Pinecone metadata to mark as selected for this session (in background)
    try {
      await markChunkSelected(currentChunk.id);
    } catch (error) {
      console.error('Error marking chunk as selected:', error);
      // On error, restore the chunk to the list
      setChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    }
  }, [currentChunk, chunks, currentIndex, markChunkSelected]);

  const handleDeletePermanently = useCallback(async () => {
    if (!currentChunk) return;

    setShowDeleteConfirm(false);
    setIsDeleting(true);

    // Optimistically update UI immediately
    const newChunks = chunks.filter((_, index) => index !== currentIndex);
    setChunks(newChunks);

    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Use conditional unmark - only decrements count if chunk was actually selected
    const unmarkPromise = unmarkChunkSelectedConditionally(currentChunk.id);

    try {
      await Promise.all([
        apiService.deleteRecord(currentChunk.id, userId),
        unmarkPromise
      ]);
    } catch (error) {
      console.error('Error deleting:', error);
      // On error, restore the chunk to the list
      setChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    } finally {
      setIsDeleting(false);
    }
  }, [currentChunk, chunks, currentIndex, userId, unmarkChunkSelectedConditionally]);

  const handleMarkDone = async () => {
    if (!currentChunk) return;

    setIsMarkingDone(true);

    // Optimistically update UI immediately
    const newChunks = chunks.filter((_, index) => index !== currentIndex);
    setChunks(newChunks);

    if (currentIndex >= newChunks.length && newChunks.length > 0) {
      setCurrentIndex(newChunks.length - 1);
    }

    // Use conditional unmark - only decrements count if chunk was actually selected
    const unmarkPromise = unmarkChunkSelectedConditionally(currentChunk.id);

    try {
      await Promise.all([
        apiService.markDone([currentChunk.id], userId),
        unmarkPromise
      ]);
    } catch (error) {
      console.error('Error marking done:', error);
      // On error, restore the chunk to the list
      setChunks([...newChunks.slice(0, currentIndex), currentChunk, ...newChunks.slice(currentIndex)]);
    } finally {
      setIsMarkingDone(false);
    }
  };

  const navigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const navigateNext = useCallback(() => {
    if (currentIndex < chunks.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, chunks.length]);

  const handleChunkUpdate = (oldChunkId: string, updatedChunk: Chunk) => {
    // Optimistically update the chunk in the list
    setChunks(prevChunks =>
      prevChunks.map(chunk =>
        chunk.id === oldChunkId ? updatedChunk : chunk
      )
    );
  };

  const handleScrollPositionChange = (chunkId: string, scrollPosition: number) => {
    setScrollPositions(prev => ({
      ...prev,
      [chunkId]: scrollPosition
    }));
  };

  // Keyboard navigation for modal
  useEffect(() => {
    if (!showDeleteConfirm) return;

    const handleModalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        // Toggle focus between delete and cancel buttons
        if (document.activeElement === deleteButtonRef.current) {
          cancelButtonRef.current?.focus();
        } else {
          deleteButtonRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleModalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleModalKeyDown);
    };
  }, [showDeleteConfirm]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if modal is open
      if (showDeleteConfirm) return;

      // Ignore if any modal is open (check for modal-overlay in the DOM)
      if (document.querySelector('.modal-overlay')) return;

      // Ignore if user is typing in an input or textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
          // Select chunk
          event.preventDefault();
          handleSwipeRight();
          break;
        case 'ArrowLeft':
          // Discard for session
          event.preventDefault();
          handleSwipeLeft();
          break;
        case 'd':
        case 'D':
          // Show delete confirmation modal
          event.preventDefault();
          setShowDeleteConfirm(true);
          break;
        case 'ArrowUp':
          // Navigate to previous chunk
          event.preventDefault();
          navigatePrevious();
          break;
        case 'ArrowDown':
          // Navigate to next chunk
          event.preventDefault();
          navigateNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentChunk, currentIndex, chunks, handleSwipeRight, handleSwipeLeft, handleDeletePermanently, navigatePrevious, navigateNext, showDeleteConfirm]);

  return (
    <div className="search-page">
      {/* Offline status indicator */}
      {!isOnline && (
        <div className="offline-banner" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          padding: '10px 12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#856404',
          fontSize: '13px',
          fontWeight: 500
        }}>
          <span>📡</span> You are offline. Search, select, discard, delete, and edit features require internet connection. Previous results remain visible for navigation.
        </div>
      )}

      <div className="search-controls">
        <div className="search-bar">
          <div className="search-row-1">
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="source-filter"
              disabled={sourcesLoading}
            >
              <option value="">All Categories</option>
              {userSources.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <div className="toggles">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={includeDone}
                  onChange={(e) => setIncludeDone(e.target.checked)}
                />
                Include Done
              </label>

              <label className="toggle">
                <input
                  type="checkbox"
                  checked={includeDiscarded}
                  onChange={(e) => setIncludeDiscarded(e.target.checked)}
                />
                Include Discarded
              </label>
            </div>
          </div>

          <div className="search-input-group">
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isOnline ? "Enter search query..." : "Search unavailable offline"}
              className="search-input"
              disabled={!isOnline}
            />
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim() || !isOnline}
              className="search-button"
              title={!isOnline ? "Search requires internet connection" : ""}
            >
              {loading ? <span className="spinner">↻</span> : 'Search'}
            </button>
            <button
              onClick={handleGetLatest}
              disabled={loading || !isOnline}
              className="latest-button"
              title={!isOnline ? "Latest requires internet connection" : "Get latest 150 chunks"}
            >
              Latest
            </button>
          </div>
        </div>
      </div>

      {!loading && chunks.length === 0 && hasSearched && (
        <div className="no-results">
          <p>No chunks found. Try a different query.</p>
        </div>
      )}

      {chunks.length > 0 && (
        <div className="chunk-display">
          <div className="chunk-header">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="delete-button"
              title={!isOnline ? "Delete requires internet connection" : "Delete permanently"}
              disabled={isDeleting || isMarkingDone || !isOnline}
            >
              {isDeleting ? 'Deleting...' : 'Delete permanently'}
            </button>
            <span className="chunk-counter">
              {currentIndex + 1}/{chunks.length}
            </span>
            <button
              onClick={handleMarkDone}
              className="done-button"
              title={!isOnline ? "Mark as done requires internet connection" : "Mark as done"}
              disabled={isDeleting || isMarkingDone || !isOnline}
            >
              {isMarkingDone ? 'Marking done...' : 'Mark as done for all sessions'}
            </button>
          </div>

          <ChunkCard
            chunk={currentChunk}
            onChunkUpdate={handleChunkUpdate}
            userId={userId}
            scrollPosition={scrollPositions[currentChunk?.id] || 0}
            onScrollPositionChange={handleScrollPositionChange}
            availableSources={userSources}
          />

          <div className="action-buttons">
            <button
              onClick={handleSwipeLeft}
              className="action-button discard-action"
              title={!isOnline ? "Discard requires internet connection" : "Discard for session"}
              disabled={!isOnline}
            >
              <span className="button-main-text">➖ Discard</span>
              <span className="button-sub-text">for session</span>
            </button>
            <button
              onClick={navigatePrevious}
              disabled={currentIndex === 0}
              className="action-button nav-action"
            >
              ↑ Prev
            </button>
            <button
              onClick={navigateNext}
              disabled={currentIndex === chunks.length - 1}
              className="action-button nav-action"
            >
              ↓ Next
            </button>
            <button
              onClick={handleSwipeRight}
              className="action-button select-action"
              title={!isOnline ? "Select requires internet connection" : "Select"}
              disabled={!isOnline}
            >
              <span className="button-main-text">➕ Select</span>
              <span className="button-sub-text">for session</span>
            </button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Permanently</h3>
            <p>Are you sure you want to permanently delete this chunk?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                ref={deleteButtonRef}
                onClick={handleDeletePermanently}
                className="delete-btn"
                autoFocus
              >
                Delete
              </button>
              <button
                ref={cancelButtonRef}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SearchChunksPage;
